<template>
	<div class="add-client-modal">
		<app-loader
			v-if="isLoading"
			:style="loaderStyle"
		/>
		<div class="add-client-modal__search">
			<app-input
				ref="$input"
				v-model="filter"
				class="add-client-modal__search-input"
				:placeholder="$t('search_short')"
			/>
			<div
				v-if="props.showCloseIcon"
				class="add-client-modal__close"
			>
				<app-tooltip :text="$t('close_label')">
					<template #activator>
						<app-icon
							class="create-edit-contact__body-tags-item-delete"
							name="close"
							width="20px"
							color="#D2D2D2"
							hover-color="#499259"
							cursor="pointer"
							@click="emit('close')"
						/>
					</template>
				</app-tooltip>
			</div>
		</div>
		<div class="add-client-modal__header">
			<div class="add-client-modal__header-title">
				{{ $t('clients_system') }}
			</div>
			<div
				class="add-client-modal__header-drop-btn"
				@click="dropAllClients"
			>
				{{ $t('selector_reset') }}
			</div>
		</div>
		<div
			class="add-client-modal__body"
			ref="$body"
		>
			<div
				v-for="client in filteredClients"
				class="add-client-modal__body-item"
				:key="client.Id"
			>
				<app-checkbox
					:style="{ height: '16px', marginRight: '8px' }"
					:value="isClientAdded(client.Id)"
					text=""
					:disabled="isClientAdded(client.Id) && existClients.length === 1"
					@input="onChangeClientStage($event, client.Id)"
				/>
				<div
					class="add-client-modal__body-item-text"
					@click="onChangeClientStageFromName(client.Id)"
					v-html="getClientLabel(client)"
				/>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import {
	computed,
	nextTick,
	onMounted,
	ref,
	watch
} from "vue";
import AppCheckbox from "@/components/ui-kit/checkbox/AppCheckbox.vue";
import AppInput from "@/components/ui-kit/input/AppInput.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import { PersonApi } from "@/ api/PersonApi";
import { CommonEntityRecord } from "@/models/interfaces/common";
import { Client, clientsStore } from "@/store/clients";
import { debounce } from "lodash";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import { escapeHtml, highlightSearchText } from "@/utils/textHelpers";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";

type AddedClient = {
	Id: number;
	Name: string;
	Address: string[]
}

const props = defineProps<{
	contactId: number,
	client: Client,
	addedClients: CommonEntityRecord[],
	showCloseIcon?: boolean,
}>()

const emit = defineEmits(['close'])

const $input = ref<typeof AppInput | null>(null)
const $body = ref<HTMLDivElement | null>(null)

const store = clientsStore()

const isLoading = ref(true);
const contactId = ref<number | null>(null)
const filter = ref('')
const appliedFilter = ref('')
const take = 60
const skip = ref(0)
const canLoadNext = ref(true)
const isClientsListInit = ref(false)
const clients = ref<AddedClient[] | null>(null)
const existClients = ref<AddedClient[]>([])

const filteredClients = computed(() => {
	if (!existClients.value.length) return clients.value

	const sortedClientsList = [...existClients.value]
	clients.value?.forEach(client => {
		if (!existClients.value.find(c => c.Id === client.Id)) {
			sortedClientsList.push(client)
		}
	})

	return sortedClientsList
})

const loaderStyle = computed(() => ({
	'background-color': 'rgba(255, 255, 255, 0.5)'
}))

const getClientLabel = (client: AddedClient) => {
	const address = client.Address?.[0] ? `(${client.Address[0]})` : ''
	return highlightSearchText(appliedFilter.value, escapeHtml(`${client.Name ?? ''}${address}`))
}

const onOpen = async () => {
	try {
		existClients.value = props.addedClients.map(c => ({ ...c, Address: [''] }));
		contactId.value = props.contactId
		await onFetchClients()
		isClientsListInit.value = true
		await nextTick(() => $input.value?.focusInput())
	} catch (e) {
		console.error(e)
	}
}

const onSearch = async () => {
	$body.value?.scrollTo({
		top: 0
	});
	canLoadNext.value = true
	skip.value = 0
	await onFetchClients()
}

const debouncedSearch = debounce(onSearch, 500)

const onFetchClients = async () => {
	try {
		if (canLoadNext.value) {
			isLoading.value = true
			const requestedFilter = filter.value
			const clientsArray = await PersonApi.getAttachClientsList(
				contactId.value as number,
				requestedFilter,
				skip.value,
				take
			)
			appliedFilter.value = requestedFilter
			clients.value = skip.value === 0
				? clientsArray
				: clients.value
					? [...clients.value, ...clientsArray]
					: clientsArray
			skip.value += take

			if (clientsArray.length < take) {
				canLoadNext.value = false
			}
		}
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const onAddClientToContact = async (clientId: number) => {
	try {
		isLoading.value = true
		await store.attachClientToContact(props.client, clientId)
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const onDeleteClientFromContact = async (clientId: number) => {
	try {
		isLoading.value = true
		await store.detachClientFromContact(props.client, clientId)
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const dropAllClients = async () => {
	try {
		isLoading.value = true
		await store.detachAllClientsFromContact(
			props.client,
			existClients.value.slice(1).map(client => client.Id),
			existClients.value[0].Id
		)
		existClients.value = props.addedClients.map(c => ({ ...c, Address: [''] }));
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false
	}
}

const isClientAdded = (clientId: number) => {
	return !!existClients.value.find(client => client.Id === clientId)
}

const onChangeClientStage = async (stage: boolean, clientId: number) => {
	if (clients.value) {
		if (stage) {
			await onAddClientToContact(clientId)
			existClients.value.push(clients.value.find(client => client.Id === clientId)!)
		} else if (existClients.value.length > 1) {
			await onDeleteClientFromContact(clientId)
			existClients.value.splice(existClients.value.findIndex(client => client.Id === clientId), 1)
		}
	}
}

const onChangeClientStageFromName = async (clientId: number) => {
	if (clients.value) {
		if (existClients.value.find(client => client.Id === clientId)) {
			if (existClients.value.length > 1) {
				await onDeleteClientFromContact(clientId)
				existClients.value.splice(existClients.value.findIndex(client => client.Id === clientId), 1)
			}
		} else {
			await onAddClientToContact(clientId)
			existClients.value.push(clients.value.find(client => client.Id === clientId)!)
		}
	}
}

watch(filter, (newVal, oldVal) => {
	if (newVal !== oldVal) {
		debouncedSearch()
	}
})

watch(isClientsListInit, (newValue) => {
	if (newValue) {
		nextTick(() => {
			$body.value?.addEventListener('scroll', async () => {
				if (canLoadNext.value && !isLoading.value) {
					if ($body.value?.scrollTop! + $body.value?.offsetHeight! > ($body.value?.scrollHeight! - 500)) {
						await onFetchClients()
					}
				}
			})
		})
	}
})

onMounted(() => onOpen())
</script>

<style scoped lang="scss">
.add-client-modal {
	display: flex;
	flex-direction: column;
	position: relative;
	height: 100%;
	padding: 12px 0 0px 12px;

	&__search {
		display: flex;
		gap: 8px;
		width: 100%;
		margin-bottom: 8px;
		padding-right: 12px;

		&-input {
			width: 100%;
		}
	}

	&__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 8px;
		padding-right: 12px;
		color: #979797;

		&-title {
			font-size: 12px;
			line-height: 16px;
			font-weight: 600;
		}

		&-drop-btn {
			font-size: 14px;
			line-height: 18px;
			cursor: pointer;

			&:hover {
				color: #438451;
				text-decoration: underline;
			}
		}
	}

	&__body {
		display: flex;
		flex: 1;
		flex-direction: column;
		gap: 8px;
		overflow: auto;

		&-item {
			display: flex;
			font-size: 14px;
			line-height: 18px;
			color: #4D4D4D;
			cursor: pointer;
			transition: color 0.1s;

			&-text {
				font-size: 14px;
				line-height: 18px;
			}

			&:hover {
				text-decoration: underline;
				color: #438451;
			}
		}
	}
}
</style>

